import { Box, Button, IconButton, SvgIcon, Tooltip } from "@mui/material";
import { useAppSelector } from "app/hooks";
import Header from "components/header/Header";
import List from "components/list/List";
import AskForConfirmation from "components/modals/AskForConfirmation";
import { selectCertificateTypes } from "features/certificateTypes/certificateTypesSlice";
import { Add, Delete, Edit } from "icons";
import React from "react";

interface SellerCertificateListProps {
  certificateList?: any[];
  handleEditCertificate: (row: any) => void;
  handleDeleteCertificate: (row: any, key: any) => void;
  handleAddCertificate: () => void;
}

const TableHeader: {
  [key: string]: string;
} = {
  shortname: "Zertifikat",
  certificate_number: "Zertifikat-Nr.",
  logo_registration_number: "LogoRegNr.",
  valid_from: "Gültig von",
  valid_to: "Gültig bis",
  actions: "Bearbeiten",
};

export function SellerCertificateList(props: SellerCertificateListProps) {
  const {
    certificateList,
    handleEditCertificate,
    handleDeleteCertificate,
    handleAddCertificate,
  } = props;
  const [openRequestToDelete, setOpenRequestToDelete] = React.useState(false);
  const [rowToDelete, setRowToDelete] = React.useState<any>(null);
  const [keyToDelete, setKeyToDelete] = React.useState<any>(null);

  function cancelRequestToDelete() {
    setOpenRequestToDelete(false);
  }

  function acceptDeleteRequest() {
    handleDeleteCertificate(rowToDelete, keyToDelete);
    setOpenRequestToDelete(false);
  }

  function setupDeleteRequest(row: any, key: any) {
    setRowToDelete(row);
    setKeyToDelete(key);
    setOpenRequestToDelete(true);
  }

  const certificateTypes = useAppSelector(selectCertificateTypes);
  const renderCertificateActions = (row: any, key: any) => (
    <>
      <Tooltip title="Zertifikat bearbeiten" placement="bottom">
        <IconButton onClick={() => handleEditCertificate(row)} sx={{ mr: 1 }}>
          <SvgIcon>
            <Edit />
          </SvgIcon>
        </IconButton>
      </Tooltip>
      <Tooltip title="Zertifikat löschen" placement="bottom">
        <IconButton onClick={() => setupDeleteRequest(row, key)} color="error">
          <SvgIcon>
            <Delete />
          </SvgIcon>
        </IconButton>
      </Tooltip>
    </>
  );

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formattedCertificateList = certificateList
    ?.map((cert) => ({
      ...cert,
      valid_from: formatDate(cert.valid_from),
      valid_to: formatDate(cert.valid_to),
      shortname:
        cert.certificate_type?.short_name ||
        certificateTypes.find((type) => type.id === cert.certificate_type_id)
          ?.short_name ||
        "",
    }))
    .filter((cert) => cert._destroy !== true);

  return (
    <Box sx={{ mt: 2 }}>
      <Header
        header="Zertifikatsliste"
        actionButton1={
          <Tooltip title="neues Zertifikat anlegen" placement="bottom">
            <Button
              sx={{
                backgroundColor: "#c0d904",
                color: "rgba(0,0,0,0.5)",
                borderRadius: "100%",
                minWidth: 48,
                height: 48,
                mt: 2,
                mb: 2,
                float: "right",
              }}
              onClick={handleAddCertificate}
            >
              <SvgIcon>
                <Add />
              </SvgIcon>
            </Button>
          </Tooltip>
        }
      />
      <List
        rowData={formattedCertificateList || []}
        tableHeaderList={TableHeader}
        renderRowActions={renderCertificateActions}
      />
      <AskForConfirmation
        open={openRequestToDelete}
        confirmed={acceptDeleteRequest}
        canceled={cancelRequestToDelete}
      >
        Sind Sie sicher, dass Sie das Zertifikat löschen möchten?
      </AskForConfirmation>
    </Box>
  );
}
